import React from 'react';
import {
    Typography,
    Chip,
    Box,
    Avatar,
    Link,
    FormControlLabel,
    Switch,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { _date } from "../../../utils/helpers";

function getInitial(row) {
    const first = (row.first_name || '').trim()[0];
    const last = (row.last_name || '').trim()[0];
    return (first || last || '?').toUpperCase();
}

/**
 * @param {string} locationSearch e.g. `?token=...` from useLocation().search
 * @param {(row: object) => void} [onToggleShowHome]
 */
export const TeacherColumns = (locationSearch = '', onToggleShowHome) => [
    {
        id: "name",
        label: "Name",
        render: (value, row) => {
            const initial = getInitial(row);
            const profileTo = `/teachers/${row.id}${locationSearch || ''}`;
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {row.avatar ? (
                        <Avatar
                            src={row.avatar}
                            alt={`${row.first_name} ${row.last_name}`}
                            sx={{ width: 30, height: 30, fontSize: 12, flexShrink: 0 }}
                        />
                    ) : (
                        <Avatar sx={{ width: 30, height: 30, fontSize: 12, flexShrink: 0 }}>
                            {initial}
                        </Avatar>
                    )}
                    <Box>
                        <Link
                            component={RouterLink}
                            to={profileTo}
                            underline="hover"
                            color="inherit"
                            variant="body2"
                            sx={{ fontWeight: 500, display: 'inline-block' }}
                        >
                            {row.first_name} {row.last_name}
                        </Link>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            {row.email}
                        </Typography>
                    </Box>
                </Box>
            );
        }
    },
    {
        id: "position",
        label: "Position",
        render: (value, row) => (
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
                {row.position?.trim() || "—"}
            </Typography>
        )
    },
    {
        id: "country",
        label: "Country",
        render: (value, row) => (
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
                {row.country_name || row.country || "—"}
            </Typography>
        )
    },
    {
        id: "city",
        label: "City",
        render: (value, row) => (
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
                {row.city || "—"}
            </Typography>
        )
    },
    {
        id: "classrooms_count",
        label: "Classrooms",
        render: (value, row) => (
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
                {row.classrooms_count ?? 0}
            </Typography>
        )
    },
    {
        id: "show_home",
        label: "Show on Home",
        render: (value, row) => (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                        <Switch
                            size="small"
                            checked={Boolean(row.show_home)}
                            onChange={() => onToggleShowHome?.(row)}
                            disabled={!onToggleShowHome}
                        />
                    }
                    label={
                        <Typography variant="caption" color="text.secondary">
                            {row.show_home ? "Yes" : "No"}
                        </Typography>
                    }
                    labelPlacement="end"
                />
            </Box>
        )
    },
    {
        id: "timezone",
        label: "Timezone",
        render: (value, row) => (
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
                {row.timezone || "—"}
            </Typography>
        )
    },
    {
        id: "email_verified_at",
        label: "Email Verified",
        render: (value, row) => {
            return (
                <Box sx={{ textAlign: 'center' }}>
                    <Chip 
                        label={value ? "Verified" : "Not Verified"} 
                        color={value ? "success" : "warning"} 
                        size="small" 
                    />
                </Box>
            );
        }
    },
    {
        id: "created_at",
        label: "Created Date",
        render: (value, row) => {
            return (
                <Typography sx={{ color: "text.secondary", textAlign: 'center' }}>
                    {_date(value, { relative: true })}
                </Typography>
            );
        }
    },
];
